import React from 'react'
import { Modal } from 'react-bootstrap'
import { Kpis } from '../core/types'
import {
  BAND_ORDER,
  BAND_TITLE,
  METRICS,
  Metric,
  MetricBand,
  metricBand,
  metricState,
  metricThreshold,
} from '../core/metrics'
import { METRIC_HINTS } from '../core/hints'
import InfoHint from '../../../../../common/InfoHint'

type Props = {
  show: boolean
  kpis?: Kpis
  onClose: () => void
}

// One metric as a row: the number and the rule that judges it sit together, so
// "why is this red" needs no second lookup. Same visual idiom as the alert rows
// in the health banner — a colored marker carries the state, never a word.
const MetricRow: React.FC<{ metric: Metric; kpis: Kpis }> = ({ metric, kpis }) => {
  const state = metricState(metric, kpis)
  const sub = metric.sub?.(kpis)
  return (
    <div className='d-flex align-items-center py-3 border-bottom border-gray-200'>
      <span className={`bullet bullet-vertical h-40px w-4px bg-${state} me-4 flex-shrink-0`} />
      <div className='flex-grow-1 me-4'>
        <div className='fw-semibold text-gray-900 d-flex align-items-center'>
          {metric.title}
          <InfoHint text={METRIC_HINTS[metric.hintKey]} />
        </div>
        <div className='text-gray-700 fs-8'>
          {metric.analyzed}
          {sub && ` · ${sub}`}
        </div>
      </div>
      <div className='text-end flex-shrink-0'>
        <span className={`badge badge-light-${state} text-${state} fs-5 fw-bold px-3 py-2`}>
          {metric.value(kpis)}
        </span>
        <div className='text-gray-600 fs-8 mt-1'>порог {metricThreshold(metric)}</div>
      </div>
    </div>
  )
}

const MetricsModal: React.FC<Props> = ({ show, kpis, onClose }) => {
  // Group by band so the modal is read worst-first; empty bands are skipped.
  const grouped = BAND_ORDER.map((band) => ({
    band,
    metrics: kpis
      ? METRICS.filter((m) => metricBand(metricState(m, kpis)) === band)
      : ([] as Metric[]),
  })).filter((g) => g.metrics.length > 0)

  return (
    <Modal show={show} onHide={onClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-3'>
          Все метрики
          {kpis && <span className='badge badge-light-dark text-gray-800'>{kpis.currency}</span>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='text-gray-700 fs-8 mb-4'>
          Состояние каждой метрики по выбранной валюте. Порог — граница подсветки числа, а не
          условие срабатывания алерта: алерты показаны в блоке светофора выше.
        </div>

        {!kpis && <div className='text-muted'>Нет активной статистики мэтчинга</div>}

        {grouped.map(({ band, metrics }, groupIndex) => (
          <div key={band} className={groupIndex > 0 ? 'mt-6' : undefined}>
            <BandHeading band={band} count={metrics.length} />
            {metrics.map((m) => (
              <MetricRow key={m.key} metric={m} kpis={kpis as Kpis} />
            ))}
          </div>
        ))}
      </Modal.Body>
    </Modal>
  )
}

const BandHeading: React.FC<{ band: MetricBand; count: number }> = ({ band, count }) => (
  <div className='d-flex align-items-center mb-2'>
    <span className={`bullet bullet-dot bg-${band} h-8px w-8px me-2 flex-shrink-0`} />
    <span className='text-gray-800 fw-bold fs-7 text-uppercase'>{BAND_TITLE[band]}</span>
    <span className='text-gray-500 fs-8 ms-2'>{count}</span>
  </div>
)

export default MetricsModal
